import React, { useEffect, useState } from "react";
import {
  Car,
  getGearboxClassName,
  getInsuranceGroupClassName,
} from "../CarCard/CarCard";
import CarImageModal from "../CarImageModal/CarImageModal";
import "./FavoriteList.css";

const FAVORITE_ORDER_KEY = "favoriteOrder";

const readFavoriteOrder = (): number[] => {
  try {
    const storedOrder: unknown = JSON.parse(
      localStorage.getItem(FAVORITE_ORDER_KEY) || "[]"
    );
    return Array.isArray(storedOrder)
      ? storedOrder.filter((id): id is number => typeof id === "number")
      : [];
  } catch {
    return [];
  }
};

interface Props {
  allCars: Car[];
  favorites: number[];
  setFavorites: React.Dispatch<React.SetStateAction<number[]>>;
  isReordering: boolean;
  onReorderingChange: (isReordering: boolean) => void;
}

const FavoriteList: React.FC<Props> = ({
  allCars,
  favorites,
  setFavorites,
  isReordering,
  onReorderingChange,
}) => {
  const favoriteCars = allCars.filter((car) => favorites.includes(car.id));
  const [savedOrder, setSavedOrder] = useState<number[]>(readFavoriteOrder);
  const [draftOrder, setDraftOrder] = useState<number[] | null>(null);
  const [draggedCarId, setDraggedCarId] = useState<number | null>(null);

  const orderedFavoriteCars = (() => {
    const savedPositions = new Map(
      (draftOrder ?? savedOrder).map((id, index) => [id, index])
    );
    const originalPositions = new Map(
      favoriteCars.map((car, index) => [car.id, index])
    );

    return [...favoriteCars].sort((first, second) => {
      const firstPosition = savedPositions.get(first.id);
      const secondPosition = savedPositions.get(second.id);

      if (firstPosition === undefined && secondPosition === undefined) {
        return originalPositions.get(first.id)! - originalPositions.get(second.id)!;
      }
      if (firstPosition === undefined) return 1;
      if (secondPosition === undefined) return -1;
      return firstPosition - secondPosition;
    });
  })();

  useEffect(() => {
    if (isReordering && draftOrder === null) {
      setDraftOrder(orderedFavoriteCars.map((car) => car.id));
    } else if (!isReordering && draftOrder !== null) {
      setDraftOrder(null);
      setDraggedCarId(null);
    }
  }, [isReordering, draftOrder, orderedFavoriteCars]);

  const [activeGallery, setActiveGallery] = useState<{
    images: string[];
    index: number;
    alt: string;
  } | null>(null);

  const removeFavorite = (carId: number) => {
    const updatedFavorites = favorites.filter((id) => id !== carId);
    localStorage.setItem("favorites", JSON.stringify(updatedFavorites));
    setFavorites(updatedFavorites);
  };

  const moveFavorite = (targetCarId: number) => {
    if (draggedCarId === null || draftOrder === null || draggedCarId === targetCarId) {
      return;
    }

    setDraftOrder((currentOrder) => {
      if (!currentOrder) return currentOrder;
      const nextOrder = [...currentOrder];
      const sourceIndex = nextOrder.indexOf(draggedCarId);
      const targetIndex = nextOrder.indexOf(targetCarId);
      if (sourceIndex < 0 || targetIndex < 0) return currentOrder;

      const [movedId] = nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(targetIndex, 0, movedId);
      return nextOrder;
    });
    setDraggedCarId(null);
  };

  const saveOrder = () => {
    if (draftOrder === null) return;

    const visibleIds = new Set(favoriteCars.map((car) => car.id));
    const completeOrder = [
      ...draftOrder,
      ...favorites.filter((id) => !visibleIds.has(id) && !draftOrder.includes(id)),
    ];
    localStorage.setItem(FAVORITE_ORDER_KEY, JSON.stringify(completeOrder));
    setSavedOrder(completeOrder);
    setDraftOrder(null);
    onReorderingChange(false);
  };

  if (favoriteCars.length === 0) {
    return <p className="has-text-grey">No favourites selected yet.</p>;
  }

  const returnCarSoldBgClassName = (carSold: boolean): string =>
    carSold ? "car-sold" : "";
  // const returnCarSoldViewBtnClassName = (carSold: boolean): string =>
  //   carSold ? "is-dark" : "is-link";

  return (
    <div className="table-container">
      <table className="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            {isReordering && <th className="favorite-drag-column" aria-label="Reorder" />}
            <th>Images</th>
            <th>Make, Model, Details, Insurance Group, Gearbox</th>
            <th className="registration-area">Registration</th>
            {/* <th>Mileage</th> */}
            <th>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {orderedFavoriteCars.map((car, index) => (
            <tr
              key={car.id}
              className={`favorite-row-${index % 2 === 0 ? "even" : "odd"} ${returnCarSoldBgClassName(car.isCarSold)}`}
              onDragOver={isReordering ? (event) => event.preventDefault() : undefined}
              onDrop={isReordering ? (event) => {
                event.preventDefault();
                moveFavorite(car.id);
              } : undefined}
            >
              {isReordering && (
                <td className="favorite-drag-column">
                  <button
                    className="favorite-drag-handle"
                    type="button"
                    draggable
                    aria-label={`Drag to reorder ${car.make} ${car.model}`}
                    title="Drag to reorder"
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", String(car.id));
                      setDraggedCarId(car.id);
                    }}
                    onDragEnd={() => setDraggedCarId(null)}
                  >
                    <i className="fas fa-grip-vertical" aria-hidden="true"></i>
                  </button>
                </td>
              )}
              {/* Images column */}
              <td>
                <div className="is-flex is-flex-wrap-wrap">
                  {car.images.map((img, index) => {
                    const imageClassName: string = img.includes("front")
                      ? "first-image"
                      : "last-image";
                    return (
                      <div className="column favorite-thumbnail" key={index}>
                        <img
                          src={`images/${img}`}
                          alt={`${car.make} ${car.model} - ${car.trim}`}
                          className={`thumbnail ${imageClassName}`}
                          onClick={() =>
                            setActiveGallery({
                              images: car.images,
                              index,
                              alt: `${car.make} ${car.model} - ${car.trim}`,
                            })
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </td>

              {/* Detail columns */}
              <td>
                {car.make} {car.model} <br />
                {car.trim}
                {car.details && (
                  <div className="favorite-car-details">{car.details}</div>
                )}
                <div className="favorite-car-tags">
                  <span className={`tag ${getInsuranceGroupClassName(car.insuranceGroup, car.isCarSold)}`}>
                    Insurance group&nbsp;{car.insuranceGroup}
                  </span>
                  {car.gearbox && (
                    <span className={`tag ${getGearboxClassName(car.gearbox)}`}>
                      Gearbox&nbsp;{car.gearbox}
                    </span>
                  )}
                </div>
              </td>
              <td className="registration-area">
                {car.registration}
                {car.isCarSold && (
                  <div className="tag is-black is-medium has-text-warning">
                    SOLD
                  </div>
                )}
              </td>
              {/* <td>
                {typeof car.mileage === "number"
                  ? `${car.mileage.toLocaleString()} miles`
                  : ""}
              </td> */}
              <td>
                {/*
                <a
                  href={car.detailsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`favorite-view-button button is-small ${returnCarSoldViewBtnClassName(
                    car.isCarSold
                  )} `}
                >
                  View
                </a>
                */}

                <div
                  className="favorite-toggle mt-4"
                  onClick={() => removeFavorite(car.id)}
                >
                  <button className="button is-small is-rounded">
                    <span className="icon is-small">
                      <i
                        className="fas fa-heart"
                        style={{
                          color: "red",
                          cursor: "pointer",
                          fontSize: "1.2rem",
                          transition: "color 0.3s ease",
                        }}
                        title={"Remove from favorites"}
                      ></i>
                    </span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isReordering && (
        <div className="favorite-reorder-actions favorite-reorder-actions-bottom">
          <button className="button is-primary is-small" type="button" onClick={saveOrder}>
            Save
          </button>
          <button
            className="button is-small"
            type="button"
            onClick={() => {
              onReorderingChange(false);
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {activeGallery && (
        <CarImageModal
          images={activeGallery.images}
          initialIndex={activeGallery.index}
          alt={activeGallery.alt}
          onClose={() => setActiveGallery(null)}
        />
      )}
    </div>
  );
};

export default FavoriteList;
